import { Component, Input, computed, effect, inject, signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { UserService } from '_services/user.service';
import { QuestionService } from '_services/question.service';
import { UnivPrefsService } from '_services/univ-prefs.service';
import { WelcomeService } from '_services/welcome.service';

import { filter, take, switchMap } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { TermsComponent } from '../terms/terms.component';
import { TitleService } from '_services/title.service';

@Component({
  selector: 'app-welcome',
  templateUrl: './welcome.component.html',
  styleUrls: ['./welcome.component.css'],
  standalone: false
})
export class WelcomeComponent {
  private titleService = inject(TitleService);
  we = inject(WelcomeService);
  questionService = inject(QuestionService);
  userService = inject(UserService);
  ups = inject(UnivPrefsService);
  router = inject(Router);
  private route = inject(ActivatedRoute);
  dialog = inject(MatDialog);

  readonly hasWelcomeIntro = computed(() => this.ups.hasContentHtml('welcomeIntro'));
  readonly hasWelcomeNotes = computed(() => this.ups.hasContentHtml('welcomeDisclaimer'));
  readonly hasSchoolContentBlock = computed(
    () => this.hasWelcomeIntro() || this.hasWelcomeNotes()
  );

  /** URL from prefs.welcomeImageClass background-image, if present. */
  readonly welcomeImageUrl = computed(() => {
    const cls = this.ups.preferences()?.welcomeImageClass as Record<string, string> | undefined;
    if (!cls) {
      return null;
    }
    const bg = cls['background-image'] ?? cls['backgroundImage'];
    if (typeof bg !== 'string' || !bg) {
      return null;
    }
    const match = /url\(\s*['"]?(.*?)['"]?\s*\)/i.exec(bg);
    const url = match?.[1]?.trim();
    return url || null;
  });

  readonly welcomeImageFailed = signal(false);

  @Input()
  set schoolId(schoolId: string) {
    this.titleService.setTitle('Welcome', schoolId);
    this.userService.schoolId = schoolId;
    this.loadSchoolAfterUser(schoolId);
  }

  @Input()
  set sessionId(sessionId: string) {
    this.we.sessionId = sessionId || '';
    if (sessionId) {
      this.we.sessionState.next('loading');
    }
  }

  @Input()
  set CBUID(cbUserId: string) {
    if (cbUserId) {
      this.userService.cbUserId = cbUserId || '';
      this.we.sessionState.next('loading');
    }
  }

  constructor() {
    this.we.route = this.route;

    // New URL (or cleared prefs) → allow the image to try loading again
    effect(() => {
      this.welcomeImageUrl();
      this.welcomeImageFailed.set(false);
    });
  }

  onWelcomeImageError(): void {
    this.welcomeImageFailed.set(true);
  }

  startLabel(sessionState: string | null): string {
    return this.questionsReady && sessionState !== 'loading'
      ? 'Get My Estimate'
      : 'Loading…';
  }

  aidQuizStartLabel(sessionState: string | null): string {
    return this.questionsReady && sessionState !== 'loading'
      ? 'GET STARTED'
      : 'Loading…';
  }

  isStartDisabled(sessionState: string | null): boolean {
    return !this.questionsReady || sessionState === 'loading';
  }

  private get questionsReady(): boolean {
    return this.questionService.isLoaded();
  }

  private loadSchoolAfterUser(schoolId: string) {
    this.userService.user.pipe(
      filter(user => !!user),
      take(1),
      switchMap(_user => {
        console.log('Loading school after user', schoolId);
        return this.we.loadSchool(schoolId);
      })
    )
      .subscribe();
  }

  showTerms(event: Event) {
    event.preventDefault();
    this.dialog.open(TermsComponent, {
      width: '1050px'
    });
  }

  start() {
    const route = this.userService.isAidQuiz ? './household-information' : './dependency';
    this.router.navigate([route], { relativeTo: this.route });
  }
}
